<template>
  <div></div>
</template>

<script>
import Vue from 'vue';
import { mapState, mapActions, mapStores } from 'pinia';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useActionStore } from '@/stores/v2/action';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import { useStore as useMirrorStore } from '@/stores/v2/mirror';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { openDefaultEditor } from 'pintura';
import { createStateSnapshot } from '@/utils/historyHelper';
import { generateEventPayloadV2 } from '@/utils/generatePriceParamsEventPayload';
import FileService from '@/services/FileService';
import {
  DoCroppingCommand,
  CroppingResult,
} from '@/web-components/import-wizard/typings/colorManagement/colorManagement';
import { createBlob, base64StringToBlob } from 'blob-util';

export default Vue.extend({
  computed: {
    ...mapStores(useShapeStore),
    ...mapState(useShapeStore, [
      'shapes',
      'activeShapeId',
      'updateShapePosition',
    ]),
    ...mapState(useActionStore, ['actions']),
    ...mapState(useProductStore, ['activePosition', 'printPositionArea']),
  },
  mounted() {
    const subtarget = document.querySelector('w2p-import-wizard-wrapper');
    const element =
      subtarget && subtarget.shadowRoot
        ? subtarget.shadowRoot.querySelector('.import-wizard-v2')
        : document.body;

    const activeShape = this.shapes.find(
      (shape) => shape.id === this.activeShapeId
    );
    const productStore = useProductStore();
    const isConverterApplied = productStore.isConverterUsed();
    const isPantone = productStore.isPantoneColorTechnique();

    const stickerEditor = openDefaultEditor(
      {
        class: 'pintura-sticker-editor',
        src:
          isConverterApplied || isPantone
            ? activeShape.pngFileWithoutConverters
            : activeShape.pngFile,
        enableButtonRevert: false,
        enableNavigateHistory: false,
        enableZoomControls: false,
        cropEnableButtonRotateLeft: false,
        cropEnableButtonFlipHorizontal: false,
        cropImageSelectionCornerStyle: 'hook',
        cropEnableCenterImageSelection: false,
        cropActiveTransformTool: 'zoom',
        cropEnableRotationInput: false,
        utils: ['crop'],
        cropInteractionFocus: 'selection',
        locale: { labelButtonExport: this.$t('tools.custom.done') },
        cropEnableInfoIndicator: true,
        cropEnableZoom: false,
      },
      element
    );

    // Update the shape when the sticker is edited
    stickerEditor.on('process', async (res) => {
      // Update the backgroundImage of the active shape
      const actionStore = useActionStore();
      const idx = this.shapeStore.shapes.findIndex(
        (p) => p.id === activeShape?.id
      );

      const shape = this.shapeStore.shapes[idx];
      const widthScaleFactor = shape.pngWidthPixels / res.imageState.crop.width;
      const heightScaleFactor =
        shape.pngHeightPixels / res.imageState.crop.height;
      const width = shape.width / widthScaleFactor;
      const height = shape.height / heightScaleFactor;

      const smallDetailsResult = shape.smallDetailsResult
        ? { ...shape.smallDetailsResult, isCheckValid: false }
        : undefined;

      const leftCroppedValue = this.getCroppedValueFromLeftSide(
        shape.pngWidthPixels,
        res.imageState.crop.x
      );

      const rightCroppedValue = this.getCroppedValueFromRightSide(
        shape.pngWidthPixels,
        res.imageState.crop.x + res.imageState.crop.width
      );

      const topCroppedValue = this.getCroppedValueFromTop(
        shape.pngHeightPixels,
        res.imageState.crop.y
      );

      const bottomCroppedValue = this.getCroppedValueFromBottom(
        shape.pngHeightPixels,
        res.imageState.crop.y + res.imageState.crop.height
      );

      actionStore.createCropAction(this.activeShapeId, {
        left: leftCroppedValue,
        right: rightCroppedValue,
        top: topCroppedValue,
        bottom: bottomCroppedValue,
      });

      const productStore = useProductStore();
      const isTraditionalTechnique = productStore.isTraditionalTechnique();

      const command = {
        croppedFromLeftPct: leftCroppedValue,
        croppedFromRightPct: rightCroppedValue,
        croppedFromBottomPct: bottomCroppedValue,
        croppedFromTopPct: topCroppedValue,
      };

      const secondPng = isTraditionalTechnique
        ? shape.fullColorTechniquePngFile
        : shape.traditionalTechniquePngFile;
      let secondCroppedPng = '';
      if (secondPng !== '') {
        const resCrop = await FileService.cropping(secondPng, command);
        secondCroppedPng = resCrop.data.pngFile;
      }
      const fullColorPng = isTraditionalTechnique
        ? base64StringToBlob(secondCroppedPng, 'image/png')
        : res.dest;
      let traditionalTechniquePng = '';
      if (
        shape.traditionalTechniquePngFile &&
        shape.traditionalTechniquePngFile !== ''
      ) {
        traditionalTechniquePng = isTraditionalTechnique
          ? res.dest
          : base64StringToBlob(secondCroppedPng, 'image/png');
      }

      this.shapeStore.$patch((state) => {
        state.shapes.splice(idx, 1, {
          ...state.shapes[idx],
          backgroundImage: URL.createObjectURL(res.dest),
          pngFileWithoutConverters: res.dest,
          pngFile: res.dest,
          fullColorTechniquePngFile: fullColorPng,
          traditionalTechniquePngFile: traditionalTechniquePng,
          height: height,
          width: width,
          aspectRatio: width / height,
          pngWidthPixels: res.imageState.crop.width,
          pngHeightPixels: res.imageState.crop.height,
          variantArtworkImages: [],
          smallDetailsResult,
        });
      });

      await this.updateColorsAfterImageIsAdjusted(this.activeShapeId, true);

      if (this.shapes[idx]) {
        await this.applyEffectIfNeeded(
          productStore.selectedPrintPosition.id,
          productStore.selectedPrintPosition.techniqueId,
          this.shapes[idx]
        );

        const activeShape = this.shapes.find(
          (shape) => shape.id === this.activeShapeId
        );
        const scaledArea = this.printPositionArea(
          this.activePosition.id || '',
          true
        );

        const productArea = this.activePosition.printArea;

        if (activeShape && scaledArea && productArea) {
          this.updateShapePosition(activeShape, scaledArea, productArea);
        }
      }

      if (this.shapes[idx]) {
        this.updateMirrorShape(this.shapes[idx]);
      }

      const eventPayload = generateEventPayloadV2();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });

      this.checkIsShapeInArea(activeShape?.id);

      this.runValidators();

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);

      this.$emit('close:crop-editor');
    });

    stickerEditor.on('close', () => {
      this.$emit('close:crop-editor');
    });
  },
  methods: {
    ...mapActions(useShapeStore, [
      'updateColorsAfterImageIsAdjusted',
      'applyEffectIfNeeded',
      'checkIsShapeInArea',
    ]),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useMirrorStore, ['updateMirrorShape']),
    ...mapActions(useNotificationStore, ['runValidators']),

    getCroppedValueFromLeftSide(originalWidth, croppedValue) {
      if (croppedValue === 0) {
        return 0;
      }
      //percantage cropped shape value from left side
      return (croppedValue * 100) / originalWidth;
    },
    getCroppedValueFromRightSide(originalWidth, croppedValue) {
      const croppedValuePixels = originalWidth - croppedValue;
      if (croppedValuePixels === 0) {
        return 0;
      }
      //percantage cropped shape value from right side
      return (croppedValuePixels * 100) / originalWidth;
    },
    getCroppedValueFromTop(originalHeight, croppedValue) {
      if (croppedValue === 0) {
        return 0;
      }
      //percantage cropped shape value from top
      return (croppedValue * 100) / originalHeight;
    },
    getCroppedValueFromBottom(originalHeight, croppedValue) {
      const croppedValuePixels = originalHeight - croppedValue;
      if (croppedValuePixels === 0) {
        return 0;
      }
      //percantage cropped shape value from right side
      return (croppedValuePixels * 100) / originalHeight;
    },
  },
});
</script>

<style scoped></style>
